import { environment } from './../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, switchMap } from 'rxjs/operators';
import { Observable, from, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import jwt_decode from 'jwt-decode';
import { Router } from '@angular/router';
import { ScreenTimeTrackerService } from './services/screen-time/screen-time-tracker.service';
import { SessionEventsService } from './services/session/session-events.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isLoggingOut = false; // Flag para prevenir navegación durante logout

  constructor(
    private httpClient: HttpClient,
    private cookieService: CookieService,
    public router: Router,
    private screenTimeTracker: ScreenTimeTrackerService,
    private sessionEvents: SessionEventsService
  ) { }

  canActivate(): boolean {
    let canActivate = true;

    // Si está en proceso de logout, permitir navegación sin interferir
    if (this.isLoggingOut) {
      return false;
    }

    if (!this.isAuthenticated()) {
      this.router.navigate(['/signin']);
      canActivate = false;
    }

    return canActivate;
  }

  public isAuthenticated(): boolean {
    const token = this.cookieService.get('gr_access');

    let isAuthenticated = false;

    // Acá había un bloque que, si el hash de la URL tenía un '=', guardaba
    // como `gr_refresh` y `gr_access` los pedazos que quedaban al partirlo y
    // daba la sesión por válida. Era de un login por redirección que esta app
    // no tiene: la sesión la escribe `signin-page` con la respuesta del login,
    // que es el único otro lugar donde se escribe `gr_refresh`.
    //
    // Rompía el link de acceso del cliente. Brevo le agrega sus UTM al final
    // del destino y, con hash routing, eso cae DENTRO del hash:
    //
    //   #/password-reset/<token>?utm_source=sendinblue&utm_campaign=...
    //
    // o sea que el front se fabricaba una sesión con el nombre de la campaña
    // de mailing de `gr_access`, el interceptor lo mandaba como Bearer, el
    // backend contestaba 401 y la rama 401 del interceptor te echaba al login.
    // La pantalla de crear la contraseña se veía menos de un segundo. Pasaba
    // igual en incógnito y en cada click, porque la cookie se rehacía desde la
    // URL en cada carga.
    //
    // Y de paso era un agujero: cualquiera podía fijarle a otro el `gr_access`
    // mandándole una URL armada.

    if (token) {
      isAuthenticated = true;
    }
    return isAuthenticated;
  }

  signIn(user: any): Observable<any> {
    const url = environment.apiUrl + '/authentication/login';
    return this.httpClient.post(url, user).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  logout(refreshToken: { refresh: string }): Observable<any> {
    // Activar flag para prevenir que canActivate() interfiera
    this.isLoggingOut = true;

    // Avisar antes de arrancar (el request al back puede tardar): todo lo que
    // sobrevive en memoria a la navegación al login —los tours de onboarding,
    // los popups de feedback pendientes— tiene que cortarse ya, o le queda
    // pegado encima de la pantalla de login.
    this.sessionEvents.notifySessionEnded();

    const url = environment.apiUrl + '/authentication/logout';
    const options = {
      headers: {
        Authorization: 'Bearer ' + this.cookieService.get('gr_access'),
      },
    };

    // Flush screen time first (awaited), then call logout endpoint so the
    // cookie is still valid when the track-time request goes out
    return from(this.screenTimeTracker.flushAndStop()).pipe(
      switchMap(() => this.httpClient.post(url, refreshToken, options)),
      catchError((error) => throwError(() => error))
    );
  }

  // Método para resetear el flag (llamado después de limpiar)
  resetLogoutFlag(): void {
    this.isLoggingOut = false;
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwt_decode(token);
    } catch {
      return null;
    }
  }

  getMe(userId: number): Observable<any> {
    const URL = `${environment.apiUrl}/users/${userId}/`;
    const handleErrors = catchError((error) => throwError(() => error));

    return this.httpClient.get(URL).pipe(handleErrors);
  }

  changePassword(
    userId: number,
    body: { password: string; new_password: string; new_password2: string }
  ): Observable<any> {
    const URL = `${environment.apiUrl}/users/change-password/${userId}`;

    return this.httpClient.put(URL, body).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  async resetPassword(
    token: string,
    password: string
  ): Promise<{ data: any; error: boolean }> {
    const URL = environment.apiUrl + '/authentication/reset-password/confirm/';
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    };

    const res = await fetch(URL, options);
    const data = await res.json();

    return { data, error: !res.ok };
  }

  /**
   * Pide el mail de "olvidé mi contraseña".
   *
   * Manda `x-client-platform` a mano porque este método usa `fetch` nativo y no
   * `HttpClient`: el AuthInterceptor —que es quien agrega esa cabecera cuando no
   * hay sesión— no intercepta `fetch`. Sin ella el backend no sabe desde qué
   * producto piden el reset y le manda el mail a cualquier cuenta que exista,
   * incluidas las de Growth Road; pasó en dev el 2026-09-16 pidiendo desde
   * app.dev.noliaedu.com el reset de una cuenta B2B, y el mail llegó.
   */
  async sendLinkToResetPassword(
    email: string
  ): Promise<{ data: any; error: boolean }> {
    const URL = environment.apiUrl + '/authentication/reset-password/';
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-client-platform': environment.clientPlatform,
      },
      body: JSON.stringify({ email }),
    };

    const res = await fetch(URL, options);
    const data = await res.json();

    return { data, error: !res.ok };
  }

  testingLTI(): Observable<any> {
    const body = new URLSearchParams();
    body.set('lti_message_type', 'basic-lti-launch-request');
    body.set('lti_version', 'LTI-1p0');
    body.set('resource_link_id', '1');
    body.set('lis_person_name_given', 'Jacobo');
    body.set('lis_person_name_family', 'Martínez');
    body.set('lis_person_contact_email_primary', 'jacobo.amn89@gmail.com');
    body.set('user_id', '9999');

    body.set('oauth_consumer_key', 'M36MKFHS8Q9RSG3EH71G26XJIMSZW7G0');
    body.set('oauth_nonce', 'PLTT99eToTR');
    body.set('oauth_signature', 'RmH8H6t66EKJfmCPwjro5S64Bk8=');
    body.set('oauth_signature_method', 'HMAC-SHA1');
    body.set('oauth_timestamp', '1666149977');
    body.set('oauth_version', '1.0');

    const options = {
      headers: new HttpHeaders().set(
        'Content-Type',
        'application/x-www-form-urlencoded'
      ),
    };

    return this.httpClient
      .post(`${environment.apiUrl}/lti/`, body.toString(), options)
      .pipe(
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }
}
